import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Quote } from "lucide-react";
import { Button } from "../ui/button";

export function PageHero({ eyebrow, title, lead, image, imageAlt, cta, secondary, children }) {
  return (
    <section className="relative overflow-hidden kt-grain">
      <div className="kt-container grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
        <div className="kt-fade-up">
          {eyebrow && <p className="kt-eyebrow">{eyebrow}</p>}
          <h1 className="kt-h1 mt-4">{title}</h1>
          {lead && <p className="kt-lead mt-6 max-w-xl">{lead}</p>}
          {(cta || secondary) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {cta && (
                <Button asChild size="lg" className="h-12 rounded-full bg-brand px-7 text-base text-white hover:bg-brand-600" data-testid="hero-cta">
                  <Link to={cta.to}>{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              )}
              {secondary && (
                <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-ink/15 px-7 text-base text-ink hover:bg-surface-200" data-testid="hero-secondary">
                  <Link to={secondary.to}>{secondary.label}</Link>
                </Button>
              )}
            </div>
          )}
          {children}
        </div>
        {image && (
          <div className="kt-fade-up kt-delay-2 relative">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-brand-100/60 blur-2xl" />
            <img src={image} alt={imageAlt || ""} className="aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-2xl shadow-ink/20" loading="eager" />
          </div>
        )}
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, lead, align = "left" }) {
  return (
    <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <p className="kt-eyebrow">{eyebrow}</p>}
      <h2 className="kt-h2 mt-3">{title}</h2>
      {lead && <p className="kt-lead mt-4">{lead}</p>}
    </div>
  );
}

export function FeatureGrid({ items, columns = 3 }) {
  const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[columns];
  return (
    <div className={`grid gap-6 ${cols}`}>
      {items.map((f, i) => (
        <div key={f.title} className={`kt-card kt-card-hover kt-fade-up p-7 kt-delay-${(i % 4) + 1}`} data-testid={`feature-${i}`}>
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-graphite-50 text-graphite">
            <f.icon className="h-6 w-6" />
          </span>
          <h3 className="mt-5 text-lg font-semibold text-ink">{f.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">{f.text}</p>
        </div>
      ))}
    </div>
  );
}

export function Steps({ steps }) {
  return (
    <ol className="grid gap-8 md:grid-cols-3">
      {steps.map((s, i) => (
        <li key={s.title} className="relative" data-testid={`step-${i}`}>
          <span className="text-6xl font-black leading-none text-brand-100">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="mt-3 text-lg font-semibold text-ink">{s.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">{s.text}</p>
        </li>
      ))}
    </ol>
  );
}

export function StoryCard({ story, featured = false }) {
  return (
    <Link to={`/stories/${story.slug}`} className={`kt-card kt-card-hover group block overflow-hidden ${featured ? "lg:col-span-2" : ""}`} data-testid={`story-card-${story.slug}`}>
      <div className={`overflow-hidden ${featured ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
        <img src={story.hero_image_url} alt={story.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
      </div>
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {story.author_role && <span className="rounded-full bg-graphite-50 px-2.5 py-1 font-medium text-graphite">{story.author_role}</span>}
          {story.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{story.location}</span>}
        </div>
        <h3 className={`mt-3 font-bold leading-snug text-ink group-hover:text-brand ${featured ? "text-2xl" : "text-lg"}`}>{story.title}</h3>
        {story.subtitle && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink-soft">{story.subtitle}</p>}
        <p className="mt-4 text-sm font-medium text-ink">{story.author_name}</p>
      </div>
    </Link>
  );
}

export function Testimonial({ quote, name, role }) {
  return (
    <figure className="kt-card p-7">
      <Quote className="h-6 w-6 text-brand-300" />
      <blockquote className="mt-4 text-base leading-relaxed text-ink">{quote}</blockquote>
      <figcaption className="mt-5 text-sm"><span className="font-semibold text-ink">{name}</span> <span className="text-ink-muted">&middot; {role}</span></figcaption>
    </figure>
  );
}

export function CtaBand({ title, text, cta, secondary, tone = "graphite" }) {
  const bg = tone === "graphite" ? "bg-graphite" : "bg-brand";
  return (
    <section className="kt-container py-16 lg:py-24">
      <div className={`${bg} kt-grain relative overflow-hidden rounded-[2rem] px-8 py-14 text-surface sm:px-14 lg:flex lg:items-center lg:justify-between`}>
        <div className="max-w-xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
          <p className="mt-4 text-base leading-relaxed text-surface/80">{text}</p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3 lg:mt-0">
          <Button asChild size="lg" className="h-12 rounded-full bg-surface px-7 text-base text-ink hover:bg-white" data-testid="cta-band-primary">
            <Link to={cta.to}>{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          {secondary && (
            <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-surface/40 bg-transparent px-7 text-base text-surface hover:bg-surface/10 hover:text-surface">
              <Link to={secondary.to}>{secondary.label}</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
